/**
 * Engagement Profile Bridge — Sprint 17g-4 Task 5.3
 *
 * Wires engagement events to:
 * 1. PredictionTracker — records hit/miss outcomes for accuracy scoring
 * 2. UserProfileStore — updates interests/anti-interests from engagement
 */

import { POSITIVE_ACTIONS, NEGATIVE_ACTIONS } from '@ownyou/shared-types';
import { PredictionTracker } from './prediction-tracker';

// Derive from canonical set + bridge-specific extensions
const HIT_ACTIONS = [...POSITIVE_ACTIONS, 'refined', 'clicked_primary'];
const MISS_ACTIONS = [...NEGATIVE_ACTIONS];

/**
 * Record a prediction outcome from a mission engagement action.
 */
export async function recordPredictionOutcome(
  tracker: PredictionTracker,
  mission: { type: string },
  action: string,
  onThresholdCrossed?: (threshold: number, tokens: number) => void,
): Promise<void> {
  const outcome = HIT_ACTIONS.includes(action) ? 'hit'
    : MISS_ACTIONS.includes(action) ? 'miss'
    : null;

  if (!outcome) return;

  const result = await tracker.recordOutcome(mission.type, outcome);
  if (result.thresholdCrossed && result.tokensAwarded) {
    onThresholdCrossed?.(result.thresholdCrossed, result.tokensAwarded);
  }
}

/** Minimal UserProfileStore interface for bridge operations */
interface ProfileStore {
  recordEngagementSignal(category: string, action: string): Promise<void>;
  mergeInterest(entity: string, confidence: number): Promise<void>;
}

/**
 * Update UserProfileStore from engagement actions.
 * This ensures mergeInterest/addAntiInterest get called from the mission pipeline,
 * feeding real data to the constellation (Task 5.6).
 */
export async function updateProfileFromEngagement(
  profileStore: ProfileStore,
  mission: { type: string; title: string; entities?: string[] },
  action: string,
): Promise<void> {
  // Positive engagement → boost interest confidence
  if (HIT_ACTIONS.includes(action)) {
    await profileStore.recordEngagementSignal(mission.type, action === 'completed' ? 'completed' : 'accepted');
    for (const entity of mission.entities ?? []) {
      await profileStore.mergeInterest(entity, 0.7);
    }
  }
  // Negative engagement → record dismissal for anti-interest detection
  if (MISS_ACTIONS.includes(action)) {
    await profileStore.recordEngagementSignal(mission.type, 'dismissed');
  }
}

export { HIT_ACTIONS, MISS_ACTIONS };
